import React from "react";
import { useAppStore } from "../shared/stores/useAppStore";
import snakeToPascal from "../tools";
import dayjs from "dayjs";
import { DATESYSTEM_TO_FORMAT } from "../pages/subpages/Settings";
import { cn } from "../lib/utils";

const StatusBadge = ({ active }: { active: boolean }) => (
    <span className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold",
        active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
    )}>
        <span className="material-symbols-outlined text-[14px]">
            {active ? 'check_circle' : 'cancel'}
        </span>
        {active ? 'Yes' : 'No'}
    </span>
);

const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-b-0">
        <span className="text-sm text-slate-500">{label}</span>
        <span className="text-sm font-medium text-slate-900">{children}</span>
    </div>
);

const CommercialPlanDetails = ({ commercialPlan }) => {
    const userSettings = useAppStore(s => s.userSettings);

    return (
        <div className="space-y-0">
            <DetailRow label="Licence">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                    Business
                </span>
            </DetailRow>
            <DetailRow label="Type">
                {snakeToPascal(commercialPlan.type)}
            </DetailRow>
            <DetailRow label="Expiry Date">
                {commercialPlan.expiry_date
                    ? dayjs(commercialPlan.expiry_date).format(DATESYSTEM_TO_FORMAT[userSettings.dateSystem])
                    : <span className="text-slate-400">Never</span>}
            </DetailRow>
            <DetailRow label="Active">
                <StatusBadge active={commercialPlan.is_currently_active} />
            </DetailRow>
            <DetailRow label="Share Inside Organization">
                <StatusBadge active={commercialPlan.can_share_inside_organization} />
            </DetailRow>
            <DetailRow label="Share Outside Organization">
                <StatusBadge active={commercialPlan.can_share_outside_organization} />
            </DetailRow>
            <DetailRow label="Scenarios per Model">
                {commercialPlan.number_scenario_max_per_model}
            </DetailRow>
            <DetailRow label="Sensitivities per Scenario">
                {commercialPlan.number_sensitivities_max_per_scenario}
            </DetailRow>
            <DetailRow label="Operational Access">
                <StatusBadge active={commercialPlan.bpo_access} />
            </DetailRow>
            {commercialPlan.type == 'subscription' && (
                <DetailRow label="Models per Year">
                    {commercialPlan.model_max_number_per_year}
                </DetailRow>
            )}
            {commercialPlan.type == 'unitary' && (
                <DetailRow label="Model Count">
                    {commercialPlan.model_count}
                </DetailRow>
            )}
            {commercialPlan.type == 'trial' && (
                <DetailRow label="Max Model Count">
                    {commercialPlan.model_max_number}
                </DetailRow>
            )}
            {commercialPlan.type == 'guest' && (
                <DetailRow label="Max Model Count">
                    {commercialPlan.model_per_account}
                </DetailRow>
            )}
        </div>
    );
}


export default function CurrentCommercialPlan() {
    const commercialPlan = useAppStore(s => s.commercialPlan);

    if (commercialPlan && commercialPlan.is_currently_active) {
        return (
            <CommercialPlanDetails commercialPlan={commercialPlan}/>
        );
    } else {
        return (
            <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Licence:</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                    Standard
                </span>
            </div>
        )
    }
};

export { CommercialPlanDetails }
